import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { ApiError } from '../../api/problem'
import type { components } from '../../api/generated/schema'
import { useCampaignSheet } from '../../domains/campaigns/queries'
import { useCreateEncounter, useEncounterList } from '../../domains/encounters/queries'
import { LocationPicker } from '../../domains/encounters/sections/LocationPicker'
import { ArcaneCircle, Banner, Button, Card, Input, SectionLabel } from '../../design/components'
import styles from './play.module.css'

type Schema = components['schemas']

/**
 * A campaign's encounters — **Game Master only, reads included.**
 *
 * The API answers a player with 403 rather than an empty list, because a participant's
 * card is an entire NPC sheet. So this page checks the campaign's own `isGameMaster`
 * before mounting the query at all, and a player who arrives by URL gets the same empty
 * state a stranger would rather than a failed request and an error banner.
 */
export function EncounterListPage() {
  const params = useParams<{ id: string }>()
  const campaignId = Number(params.id)

  const campaign = useCampaignSheet(campaignId)
  const isGameMaster = campaign.data?.isGameMaster === true

  const [creating, setCreating] = useState(false)
  const query = useEncounterList(campaignId, isGameMaster)

  const encounters = query.data?.items ?? []
  const campaignName = campaign.data?.sections.core?.name

  if (campaign.isSuccess && !isGameMaster) {
    return (
      <AppShell title="Encounters" subtitle="NOT YOURS TO SEE">
        <Card>
          <Banner tone="info">
            Encounters are the Game Master&rsquo;s. Every card in one is an NPC&rsquo;s full sheet,
            so the API does not show them to players.
          </Banner>
        </Card>
        <Link to={`/campaigns/${campaignId}`} className={styles.backLink}>
          <Button variant="secondary" fullWidth>
            Back to the campaign
          </Button>
        </Link>
      </AppShell>
    )
  }

  return (
    <AppShell title="Encounters" subtitle={campaignName?.toUpperCase() ?? 'GAME MASTER ONLY'}>
      {(campaign.isPending || query.isPending) && <Card>Loading…</Card>}

      {campaign.isError && (
        <Card>
          <Banner tone="info">No such campaign, or it is not one of yours.</Banner>
        </Card>
      )}

      {query.isError && (
        <Card>
          <Banner tone="error" urgent>
            Could not load this campaign&rsquo;s encounters.
          </Banner>
        </Card>
      )}

      {query.isSuccess && encounters.length === 0 && !creating && (
        <Card label="No encounters yet">
          <p className={styles.note}>
            An encounter is two or more sides and the people on them, with every participant&rsquo;s
            combat card worked out for you.
          </p>
        </Card>
      )}

      {encounters.length > 0 && (
        <ul className={styles.list}>
          {encounters.map((encounter) => (
            <li key={encounter.id}>
              <Link to={`/encounters/${encounter.id}`} className={styles.row}>
                <ArcaneCircle size={36} className={styles.sigil} />
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{encounter.name}</span>
                  <span className={styles.rowMeta}>
                    {[
                      String(encounter.type ?? ''),
                      `${encounter.sideCount ?? 0} sides`,
                      `${encounter.participantCount ?? 0} in play`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isGameMaster &&
        (creating ? (
          <CreateEncounterCard campaignId={campaignId} onDone={() => setCreating(false)} />
        ) : (
          <Button fullWidth onClick={() => setCreating(true)}>
            New encounter
          </Button>
        ))}

      <Link to={`/campaigns/${campaignId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the campaign
        </Button>
      </Link>
    </AppShell>
  )
}

/**
 * Sides are named up front, mirroring the Blazor create modal. Leaving them blank is
 * legitimate — the API then creates its two defaults — so the field is optional rather
 * than pre-filled with names somebody has to clear.
 */
function CreateEncounterCard({ campaignId, onDone }: { campaignId: number; onDone: () => void }) {
  const navigate = useNavigate()
  const create = useCreateEncounter(campaignId)

  const [name, setName] = useState('')
  const [type, setType] = useState<Schema['EncounterType']>('Combat')
  const [sides, setSides] = useState('')
  const [locationId, setLocationId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    const sideNames = sides
      .split(',')
      .map((side) => side.trim())
      .filter((side) => side !== '')

    try {
      const sheet = await create.mutateAsync({
        name,
        type,
        ...(sideNames.length === 0 ? {} : { sideNames }),
        locationId,
      })
      void navigate(`/encounters/${sheet.encounterId}`)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create the encounter.')
    }
  }

  return (
    <Card variant="fate" label="New encounter">
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Type
          </SectionLabel>
          <select
            className={styles.select}
            value={type}
            onChange={(e) => setType(e.target.value as Schema['EncounterType'])}
          >
            <option value="Combat">Combat</option>
            <option value="Social">Social</option>
          </select>
        </label>

        <Input
          label="Sides"
          placeholder="Wardens, Red Court"
          hint="Comma-separated. Leave blank for two default sides."
          value={sides}
          onChange={(e) => setSides(e.target.value)}
        />

        <LocationPicker
          campaignId={campaignId}
          value={locationId}
          onChange={setLocationId}
          hint="Optional, and changeable later. A fight at a place with a battle map can be run as a live session everyone watches."
        />

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
